import { useEffect, useState } from "react";
import { CheckCircle, AlertCircle, AlertTriangle, Info, X } from "lucide-react";

const icons = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const colors = {
  success: { bg: "#065f46", border: "#059669", icon: "#34d399", text: "#d1fae5" },
  error: { bg: "#7f1d1d", border: "#dc2626", icon: "#f87171", text: "#fee2e2" },
  warning: { bg: "#78350f", border: "#d97706", icon: "#fbbf24", text: "#fef3c7" },
  info: { bg: "#1e3a5f", border: "#2563eb", icon: "#60a5fa", text: "#dbeafe" },
};

const colorsLight = {
  success: { bg: "#ecfdf5", border: "#86efac", icon: "#16a34a", text: "#14532d" },
  error: { bg: "#fef2f2", border: "#fca5a5", icon: "#dc2626", text: "#7f1d1d" },
  warning: { bg: "#fffbeb", border: "#fcd34d", icon: "#d97706", text: "#78350f" },
  info: { bg: "#eff6ff", border: "#93c5fd", icon: "#2563eb", text: "#1e3a5f" },
};

function getThemeColors(type) {
  const theme = document.documentElement.getAttribute("data-theme");
  return theme === "light" ? colorsLight[type] : colors[type];
}

export function Toast({ id, type = "info", message, onClose, duration = 4000 }) {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    const timer = setTimeout(() => handleClose(), duration);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]);

  function handleClose() {
    setExiting(true);
    setTimeout(() => onClose(id), 300);
  }

  const c = getThemeColors(type);
  const Icon = icons[type];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "12px 16px",
        borderRadius: 8,
        border: `1px solid ${c.border}`,
        backgroundColor: c.bg,
        color: c.text,
        fontSize: 14,
        lineHeight: 1.4,
        boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
        minWidth: 280,
        maxWidth: 420,
        transform: visible && !exiting ? "translateX(0)" : "translateX(120%)",
        opacity: visible && !exiting ? 1 : 0,
        transition: "transform 0.3s ease, opacity 0.3s ease",
        pointerEvents: "auto",
      }}
    >
      <Icon size={18} color={c.icon} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1 }}>{message}</span>
      <button
        onClick={handleClose}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 2,
          color: c.text,
          opacity: 0.6,
          flexShrink: 0,
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
}

export function ToastContainer({ toasts, onClose }) {
  return (
    <div
      style={{
        position: "fixed",
        bottom: 20,
        right: 20,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column-reverse",
        gap: 8,
        pointerEvents: "none",
      }}
    >
      {toasts.map((t) => (
        <Toast key={t.id} {...t} onClose={onClose} />
      ))}
    </div>
  );
}
